import { useAuth } from "@clerk/clerk-react";
import { Grid, Slide, useTheme } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OnboardingDialogsContext } from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import {
  ENTER_DELAY,
  EXIT_DELAY,
  SlideAnimationContext,
} from "contexts/SlideAnimation";
import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import StyledDialog from "components/Styled/Dialog";
import AboutForm from "features/Auth/Onboarding/AboutForm";
import CategoryForm from "features/Auth/Onboarding/CategoryForm";
import { fetcherAuth } from "utils/api";
import { isPage } from "utils/general";
import {
  INSTAGRAM_FOLLOWED,
  INSTAGRAM_JOINED_CHANNEL,
} from "utils/localStorage";
import { getSource, trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

const styles = {
  noOverflow: {
    overflowX: "hidden",
  },
  fullWidth: {
    width: "100%",
  },
};

enum Page {
  about = "about",
  categories = "categories",
  complete = "complete",
}

const ProfileDialog = () => {
  const theme = useTheme();
  const location = useLocation();

  const { setAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { currentUser, profile, setProfile, currentOrg } = useContext(
    OrganizationUserContext,
  );
  const {
    setProfileDialogOpen,
    setInstagramOnboardingDialogOpen,
    setCreateTemplateOnboardingDialogOpen,
    noTemplates,
  } = useContext(OnboardingDialogsContext);
  const { slideOutLeft, slideInLeft, slideOutRight, slideInRight, slideProps } =
    useContext(SlideAnimationContext);
  const [page, setPage] = useState<Page>(Page.about);
  const source = getSource();

  const [onboardingForm, setOnboardingForm] = useState<Map>({});

  const saveOnboardingForm = async (onboardingForm: Map) => {
    const onboardingFormReq = {
      ...onboardingForm,
      signature: null, // do not overwrite default signature so that it updates using social links
      source,
      page,
    };

    try {
      const { organizationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding`,
        "PUT",
        {},
        onboardingFormReq,
      );

      setProfile({ ...organizationProfile });
    } catch (error) {
      if (error?.message?.includes("location")) {
        setOnboardingForm((prev) => ({
          ...prev,
          location: undefined,
          latitude: undefined,
          longitude: undefined,
          city: undefined,
          address_components: undefined,
        }));
      }
      setPage(page);
      setAlert(
        error?.message ||
          "Unable to save your information. Please reload and try again",
        "error",
      );
    }
  };

  useEffect(() => {
    if (profile?.id) {
      const copy = makeDeepCopy(profile);
      copy.name = currentUser?.name;
      setOnboardingForm(copy);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  useEffect(() => {
    slideInLeft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasClickedFollowed =
    localStorage.getItem(`${INSTAGRAM_FOLLOWED}-${currentOrg?.id}`) ===
      "true" ||
    localStorage.getItem(`${INSTAGRAM_JOINED_CHANNEL}-${currentOrg?.id}`) ===
      "true";

  const animateNext = async (currentPage: Page) => {
    await slideOutLeft();
    if (currentPage === Page.about) {
      if (
        !currentOrg?.bentoCategories ||
        currentOrg?.bentoCategories?.length === 0
      ) {
        setPage(Page.categories);
      } else {
        goToTemplates();
      }
    } else if (currentPage === Page.categories) {
      goToTemplates();
    }
    slideInLeft();
  };

  const goToTemplates = () => {
    if (
      (isPage(routes.recommendations) || isPage(routes.campaignView)) &&
      noTemplates
    ) {
      setProfileDialogOpen(false);
      setCreateTemplateOnboardingDialogOpen(true);
    } else {
      setProfileDialogOpen(false);
      if (!hasClickedFollowed) {
        setInstagramOnboardingDialogOpen(true);
      }
    }
  };

  const handleNext = async (updatedForm?: Map) => {
    if (updatedForm) {
      setOnboardingForm(updatedForm);
    }

    const currentPage = page;
    if (currentPage === Page.complete) {
      setProfileDialogOpen(false);
      return;
    }
    await animateNext(currentPage);

    trackEvent("Complete Profile Dialog - Next Button Pressed", {
      page: currentPage,
      route: location.pathname,
    });

    if (currentPage === Page.about) {
      saveOnboardingForm(updatedForm || onboardingForm);
    }
  };

  const handleBack = async () => {
    trackEvent("Complete Profile Dialog - Back Button Pressed", {
      page,
      route: location.pathname,
    });

    await slideOutRight();
    if (page === Page.about) {
      setProfileDialogOpen(false);
    } else if (page === Page.categories) {
      setPage(Page.about);
    } else {
      setPage(Page.categories);
    }
    slideInRight();
  };

  const handleClose = () => {
    trackEvent("Complete Profile Dialog Closed", {
      page,
      route: location.pathname,
    });
    setProfileDialogOpen(false);
  };

  return (
    <StyledDialog open={true} handleClose={handleClose} title="">
      <Grid container sx={[styles.noOverflow, styles.fullWidth]}>
        <Slide
          in={slideProps.in}
          direction={slideProps.direction}
          easing={{
            enter: theme.transitions.easing.easeInOut,
            exit: theme.transitions.easing.easeIn,
          }}
          timeout={{
            enter: ENTER_DELAY,
            exit: EXIT_DELAY,
          }}
        >
          <Grid item xs={12}>
            {page === Page.about && (
              <AboutForm
                onboardingForm={onboardingForm}
                setOnboardingForm={setOnboardingForm}
                handleNext={handleNext}
                hideDiscoveryQuestion
                handleBack={handleBack}
              />
            )}
            {page === Page.categories && (
              <CategoryForm
                onboardingForm={onboardingForm}
                handleBack={handleBack}
                moveNext={handleNext}
              />
            )}
          </Grid>
        </Slide>
      </Grid>
    </StyledDialog>
  );
};

export default ProfileDialog;
